/* AO Personaliser — companion to snippets/ao-personaliser.liquid.
   No dependencies. Drives the theme's native variant pipeline by checking the
   hidden Personalisation option radios and dispatching "change", so price,
   availability and URL all update through the theme's own VariantPicker. */
(() => {
  "use strict";

  const ORDINAL_RE = /^\d{1,2}(st|nd|rd|th)$/i;
  const FREE_SYMBOLS = new Set(["♥", "\u{1F43E}"]); // ♥ 🐾
  const MAX_TOKEN_CHARS = 12;

  class AOPersonaliser extends HTMLElement {
    connectedCallback() {
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => this.init(), { once: true });
      } else {
        this.init();
      }
    }

    init() {
      if (this.initialized) return;

      this.cap = parseInt(this.dataset.maxWords, 10);
      this.wordPrice = parseInt(this.dataset.wordPrice, 10);
      this.moneySymbol = this.dataset.moneySymbol || "$";
      this.freeWords = new Set(JSON.parse(this.dataset.freeWords || "[]").map((w) => w.toLowerCase()));
      this.optionPosition = this.dataset.optionPosition;
      this.optionValues = JSON.parse(this.dataset.optionValues); // [{id, label}]
      this.variantMap = JSON.parse(this.dataset.variantMap); // {label: variantId}

      const formId = this.closest("product-rerender")?.getAttribute("observe-form");
      this.form = document.forms[formId] || document.querySelector('form[id^="product-form-main-"]');
      if (!this.form || Number.isNaN(this.cap)) return;
      this.initialized = true;
      // NB: this.form.id would return the form's variant <input name="id">
      // (named controls shadow HTMLFormElement properties) — keep the string.
      this.formId = this.form.getAttribute("id");

      // The theme's partial re-render replaces "liquid" blocks on every variant
      // change, which would recreate this widget and wipe the customer's text.
      // Opting our wrapper out keeps the widget alive; price/picker/buy-buttons
      // still re-render natively.
      this.closest("[data-block-type]")?.removeAttribute("data-block-allow-rerender");

      // label → tier ("No words" → 0, "2 words" → 2), tier → option value id / variant id
      this.tierByLabel = new Map();
      this.optionValueByTier = new Map();
      this.variantByTier = new Map();
      for (const { id, label } of this.optionValues) {
        const tier = this.parseTier(label);
        if (tier === null) continue;
        this.tierByLabel.set(label, tier);
        this.optionValueByTier.set(tier, { id, label });
        if (this.variantMap[label]) this.variantByTier.set(tier, this.variantMap[label]);
      }

      this.paramName = `${this.formId}-option${this.optionPosition}`;

      // Associate our line-item property inputs with the product form.
      this.props = {};
      for (const input of this.querySelectorAll("[data-ao-prop]")) {
        input.setAttribute("form", this.formId);
        this.props[input.dataset.aoProp] = input;
      }

      // Hide every rendering of the Personalisation picker tied to this form
      // (main picker is also hidden by CSS; this covers sticky bars etc.).
      for (const radio of document.querySelectorAll(
        `input[data-option-position="${this.optionPosition}"][form="${this.formId}"]`
      )) {
        const fieldset = radio.closest("fieldset.variant-picker__option, fieldset");
        if (fieldset) fieldset.hidden = true;
      }

      this.textInput = this.querySelector("[data-ao-text]");
      this.pillsEl = this.querySelector("[data-ao-pills]");
      this.errorEl = this.querySelector("[data-ao-error]");
      this.captionEl = this.querySelector("[data-ao-caption]");
      this.colourNameEl = this.querySelector("[data-ao-colour-name]");
      this.artistCheckbox = this.querySelector("[data-ao-artist]");
      this.commentsToggle = this.querySelector("[data-ao-comments-toggle]");
      this.yearInput = this.querySelector("[data-ao-year]");
      this.yearToggle = this.querySelector("[data-ao-year-toggle]");
      this.commentsInput = this.querySelector("[data-ao-comments]");
      this.blockReason = null;

      this.textInput.addEventListener("input", () => this.update());

      for (const btn of this.querySelectorAll("[data-ao-symbol]")) {
        btn.addEventListener("click", () => this.insertSymbol(btn.dataset.aoSymbol));
      }

      for (const radio of this.querySelectorAll("[data-ao-colour]")) {
        radio.addEventListener("change", () => {
          this.artistCheckbox.checked = false;
          this.update();
        });
      }
      this.artistCheckbox.addEventListener("change", () => {
        if (this.artistCheckbox.checked) {
          for (const radio of this.querySelectorAll("[data-ao-colour]")) radio.checked = false;
        }
        this.update();
      });

      // Year is opt-in: unticked + greyed by default. Ticking fills the current
      // year (still editable). A ticked year can never silently fail to apply:
      // an invalid entry snaps back to the current year on blur / add to cart.
      this.yearToggle.addEventListener("change", () => {
        this.applyYearToggle();
        this.update();
      });
      this.yearInput.addEventListener("input", () => this.update());
      this.yearInput.addEventListener("blur", () => {
        this.normaliseYear();
        this.update();
      });
      this.applyYearToggle(); // reconcile with any browser-restored form state
      // Comments follow the same opt-in pattern: unticked + greyed by default,
      // ticking unlocks the field, unticking clears it so nothing hidden is sent.
      this.commentsToggle.addEventListener("change", () => {
        this.applyCommentsToggle();
        if (this.commentsToggle.checked) this.commentsInput.focus({ preventScroll: true });
        this.update();
      });
      this.applyCommentsToggle(); // reconcile with any browser-restored form state
      this.commentsInput.addEventListener("input", () => this.update());

      // Block submission (incl. AJAX add-to-cart) while invalid.
      this.form.addEventListener(
        "submit",
        (event) => {
          this.normaliseYear();
          this.syncProperties();
          if (this.blockReason) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        },
        { capture: true }
      );

      // Buy-buttons block is swapped out on each variant re-render — re-apply state after.
      this.form.addEventListener("product:rerender", () => {
        requestAnimationFrame(() => this.applyButtonState());
      });

      this.update();
    }

    applyYearToggle() {
      const on = this.yearToggle.checked;
      this.yearInput.disabled = !on;
      if (!on) this.yearInput.value = "";
      else this.normaliseYear();
    }

    applyCommentsToggle() {
      const on = this.commentsToggle.checked;
      this.commentsInput.disabled = !on;
      if (!on) this.commentsInput.value = "";
    }

    // When ticked, make sure the box holds a valid 4-digit year.
    normaliseYear() {
      if (!this.yearToggle.checked) return;
      if (!/^\d{4}$/.test(this.yearInput.value.trim())) {
        this.yearInput.value = String(new Date().getFullYear());
      }
    }

    parseTier(label) {
      const l = label.trim().toLowerCase();
      if (l === "no words") return 0;
      const m = l.match(/^(\d+)\s+words?$/);
      return m ? parseInt(m[1], 10) : null;
    }

    tokenize(text) {
      return text
        .split(/\s+/)
        .filter(Boolean)
        .map((raw) => {
          const free =
            ORDINAL_RE.test(raw) || this.freeWords.has(raw.toLowerCase()) || FREE_SYMBOLS.has(raw);
          return { raw, free, over: [...raw].length > MAX_TOKEN_CHARS };
        });
    }

    money(cents) {
      return this.moneySymbol + (cents / 100).toFixed(2).replace(/\.00$/, "");
    }

    update() {
      const tokens = this.tokenize(this.textInput.value);
      const charged = tokens.filter((t) => !t.free).length;
      const overToken = tokens.find((t) => t.over);
      const overCap = charged > this.cap;

      // Pills + inline running total, with the spelling caption below them
      const totalCents = charged * this.wordPrice;
      const appearing = this.pillsEl.hidden && tokens.length > 0;
      this.pillsEl.hidden = tokens.length === 0;
      this.captionEl.hidden = tokens.length === 0;
      if (appearing) {
        this.pillsEl.classList.add("ao-personaliser__pills--appear");
        this.pillsEl.addEventListener(
          "animationend",
          () => this.pillsEl.classList.remove("ao-personaliser__pills--appear"),
          { once: true }
        );
      }
      this.pillsEl.replaceChildren(
        ...tokens.map((t) => {
          const pill = document.createElement("span");
          pill.className = "ao-personaliser__pill" + (t.over ? " ao-personaliser__pill--over" : "");
          pill.textContent = t.raw;
          const note = document.createElement("small");
          note.textContent = t.free ? "free" : `+${this.money(this.wordPrice)}`;
          pill.append(" ", note);
          return pill;
        })
      );
      if (totalCents > 0) {
        const total = document.createElement("span");
        total.className = "ao-personaliser__pills-total";
        if (totalCents !== this.lastTotalCents) total.classList.add("ao-personaliser__pills-total--pop");
        total.textContent = `= +${this.money(totalCents)}`;
        this.pillsEl.append(total);
      }
      this.lastTotalCents = totalCents;

      // Validation
      this.blockReason = overToken
        ? `'${overToken.raw}' is over ${MAX_TOKEN_CHARS} characters — we can't fit it on this ornament.`
        : overCap
          ? `Maximum ${this.cap} ${this.cap === 1 ? "word" : "words"} on this product`
          : null;
      this.errorEl.hidden = !this.blockReason;
      this.errorEl.textContent = this.blockReason || "";

      this.selectTier(Math.min(charged, this.cap));
      this.syncProperties();
      this.applyButtonState();
    }

    selectTier(tier) {
      const optionValue = this.optionValueByTier.get(tier);
      const variantId = this.variantByTier.get(tier);
      if (variantId && this.form.elements.id) this.form.elements.id.value = variantId;
      if (!optionValue) return;
      const radio = document.querySelector(
        `input[name="${this.paramName}"][value="${optionValue.id}"]`
      );
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    syncProperties() {
      const text = this.textInput.value.trim();
      const colour = this.artistCheckbox.checked
        ? "Artist's choice"
        : this.querySelector("[data-ao-colour]:checked")?.value || "";
      const year =
        this.yearToggle.checked && /^\d{4}$/.test(this.yearInput.value.trim())
          ? this.yearInput.value.trim()
          : "";
      const comments = this.commentsToggle.checked ? this.commentsInput.value.trim() : "";

      if (this.colourNameEl) this.colourNameEl.textContent = colour;
      this.classList.toggle("ao-personaliser--artist", this.artistCheckbox.checked);

      // Each property submits only when it has content; colour only makes
      // sense when something will be painted (words or a year).
      const values = {
        text,
        colour: text || year ? colour : "",
        year,
        comments,
      };
      for (const [key, input] of Object.entries(this.props)) {
        input.value = values[key];
        input.disabled = values[key] === "";
      }
    }

    applyButtonState() {
      const blocked = Boolean(this.blockReason);
      const submit = this.form.querySelector('button[type="submit"], [name="add"]');
      if (submit) {
        submit.toggleAttribute("disabled", blocked);
        submit.setAttribute("aria-disabled", String(blocked));
      }
      const dynamic = this.form.querySelector(".shopify-payment-button");
      if (dynamic) dynamic.style.display = blocked ? "none" : "";
    }

    insertSymbol(symbol) {
      const input = this.textInput;
      const start = input.selectionStart ?? input.value.length;
      const end = input.selectionEnd ?? input.value.length;
      const before = input.value.slice(0, start);
      const after = input.value.slice(end);
      // Pad so the symbol stays its own (free) token.
      const lead = before && !/\s$/.test(before) ? " " : "";
      const trail = !after || !/^\s/.test(after) ? " " : "";
      const inserted = lead + symbol + trail;
      input.value = before + inserted + after;
      const caret = start + inserted.length;
      input.focus();
      input.setSelectionRange(caret, caret);
      this.update();
    }
  }

  if (!customElements.get("ao-personaliser")) {
    customElements.define("ao-personaliser", AOPersonaliser);
  }
})();
